/* eslint-disable import/order */
import cn from 'classnames';
import Markdown from 'markdown-to-jsx';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
//components
import Hero from '../Hero';
import { Interlinking } from '../Interlinking';
import Jumbo from '../Jumbo';
import SeoData from '../Seo/SeoData';
//utils
//constants
import { COLORS } from 'core-lib/config/config';
import { ProductWidgets } from 'src/common/constants/flexibleArticle';
import { FASTAG_TABS } from '../../../../utils/tabs';
//stylesheet
import { handleBack } from 'core-lib/utils/common';
import { useState } from 'react';
import s from './FlexibleArticleV2.module.scss';
import NavbarLayout from '../../NavbarLayout';

const MajorCitiesPrice = dynamic(
  () => import('src/modules/fuelPrice/components/MajorCitiesPrice'),
  {
    ssr: true,
  }
);

const FuelWidget = dynamic(
  () => import('../../../../modules/fuelPrice/components/FuelWidget'),
  {
    ssr: true,
  }
);

const BankList = dynamic(
  () => import('../../../../modules/fastag/components/recharge/seo/BankList'),
  {
    ssr: true,
  }
);

const FastagRechargeWidget = dynamic(
  () => import('../../../../modules/fastag/components/recharge/SearchWidget'),
  {
    ssr: true,
  }
);

const ChallanWidget = dynamic(
  () => import('../../../../modules/challan/components/SearchWidget'),
  {
    ssr: true,
  }
);

const FuelTable = dynamic(
  () => import('src/modules/fuelPrice/components/FuelTable/FuelTable'),
  {
    ssr: true,
  }
);

const StateList = dynamic(
  () => import('src/modules/fuelPrice/components/StatesWidget'),
  {
    ssr: true,
  }
);

const StateFuelTable = dynamic(
  () => import('src/modules/fuelPrice/components/StateFuelTable'),
  {
    ssr: true,
  }
);

const FASTagBuy = dynamic(
  () => import('src/modules/fastag/components/buy/BuyWidget'),
  {
    ssr: false,
  }
);
const EmiCalculatorWidget = dynamic(
  () => import('src/modules/emiCalculator/components/Widget'),
  {
    ssr: true,
  }
);
const Highlights = dynamic(
  () => import('../../../../modules/static/carInsurance/Company/Highlights'),
  {
    ssr: false,
  }
);
const InsurerList = dynamic(
  () => import('../../../../modules/static/carInsurance/Company/InsurerList'),
  {
    ssr: false,
  }
);

const DrivingLicenseQuiz = dynamic(() => import('src/modules/licenseTest'), {
  ssr: true,
});
const RtoStatesList = dynamic(() => import('../RTOStatesList'), {
  ssr: true,
});
const RtoOfficeList = dynamic(() => import('../RTOOfficeList'), {
  ssr: true,
});

const AddOns = dynamic(() => import('../AddOns/AddOns'), {
  ssr: false,
});

const FuelBannerModal = dynamic(() => import('../FuelBannerModal'), {
  ssr: false,
});

type ArticleProps = {
  contentObj: any;
  subHeaderConfig?: any;
  showBuyFuelPopup?: boolean;
};

const FilterWidgetData = (dataArr: any[], key: string) => {
  const data = dataArr?.find((item) => item?.key === key);
  return data?.data;
};

export function FlexibleArticleV2({
  contentObj,
  subHeaderConfig = null,
  showBuyFuelPopup = false,
}: ArticleProps) {
  const router = useRouter();
  const [showBannerModal, setShowBannerModal] = useState(showBuyFuelPopup);

  function PageName(str: string) {
    const arr = str.split('/');
    return arr[arr.length - 1].split('-').join(' ');
  }
  const viewOptions: any = {
    footer: { show: true },
    navBar: { show: false },
    header: { show: true, activeLink: '/services/fastag' },
    mobileApplicationSchema: { show: true },

    siteNavigationSchema: {
      show: true,
      data: {
        name: PageName(router?.asPath),
        url: router?.asPath,
      },
    },

    content: { show: true },
    faq: { show: true },
    breadCrumb: {
      show: !!contentObj?.breadCrumb,
      data: contentObj?.breadCrumb,
    },
    downloadBanner: { show: true },
    testimonial: { show: true },
    pageHeader: {
      show: true,
      backBtn: { show: true, onClick: handleBack },
      title: contentObj?.titleDesktop,
      showProfile: true,
    },
    financialSchema: {
      show: !!contentObj?.financialSchema,
      data: contentObj?.financialSchema,
    },
  };
  if (subHeaderConfig) {
    viewOptions.showSubHeader = { show: true };
    viewOptions.subHeader = subHeaderConfig;
  }

  const getWidget = (slug) => {
    if (slug === ProductWidgets.FastagRecharge) return <FastagRechargeWidget />;
    if (slug === ProductWidgets.Challan) return <ChallanWidget />;
    //@ts-ignore
    if (slug === ProductWidgets.BuyFastag) return <FASTagBuy />;
    if (slug === ProductWidgets.BankListInterlinking) return <BankList />;
    if (slug === ProductWidgets.InsurerList)
      return contentObj?.companiesList ? ( //@ts-ignore
        <InsurerList list={contentObj?.companiesList} />
      ) : null;
    if (slug === ProductWidgets.InsuranceHighlights)
      return contentObj?.highlights && contentObj?.highlights?.length ? ( //@ts-ignore
        <Highlights data={contentObj?.companyDetails?.highlights} />
      ) : null; //@ts-ignore
    if (slug === ProductWidgets.RTOListInterlinking) return <RtoStatesList />;
    if (slug === ProductWidgets.InsuranceAddons)
      return contentObj?.addOns?.body &&
        contentObj?.addOns?.body?.length > 0 ? ( //@ts-ignore
        <AddOns addOns={contentObj?.addOns} />
      ) : null;
    if (slug === ProductWidgets.RTOOfficeListInterlinking)
      return contentObj?.offices ? ( //@ts-ignore
        <RtoOfficeList offices={contentObj?.offices} />
      ) : null;
    if (slug === ProductWidgets.EmiCalculator) return <EmiCalculatorWidget />;
    if (slug === ProductWidgets.FuelPrices)
      return (
        <FuelWidget
          data={FilterWidgetData(
            contentObj?.widgetData,
            ProductWidgets.FuelPrices
          )}
        />
      );
    if (slug === ProductWidgets.CitiesFuelPrice) return <MajorCitiesPrice />;
    if (slug === ProductWidgets.FuelPricTable)
      return (
        <FuelTable
          data={FilterWidgetData(
            contentObj?.widgetData,
            ProductWidgets.FuelPricTable
          )}
        />
      );

    if (slug === ProductWidgets.FuelPriceStateList)
      return (
        <StateList
          data={FilterWidgetData(
            contentObj?.widgetData,
            ProductWidgets.FuelPriceStateList
          )}
        />
      );
    if (slug === ProductWidgets.FuelPriceStateFuelTable)
      return (
        <StateFuelTable
          data={FilterWidgetData(
            contentObj?.widgetData,
            ProductWidgets.FuelPriceStateFuelTable
          )}
        />
      );
    if (slug === ProductWidgets.DrivingLicenseQuiz)
      return <DrivingLicenseQuiz />;
    return null;
  };
  const hero = {
    title: contentObj?.heroTitle,
    subtitle: contentObj?.heroSubtitle,
    backgroundImage: contentObj?.backgroundImage || contentObj?.heroImage,
    gradient: '',
  };
  const jumbo = {
    subheading: contentObj?.jumbo,
  };

  return (
    <>
      <SeoData data={contentObj} />
      <NavbarLayout
        financialSchema={viewOptions.financialSchema}
        header={viewOptions.header}
        footer={viewOptions.footer}
        breadCrumb={viewOptions.breadCrumb}
        mobileApplicationSchema={viewOptions.mobileApplicationSchema}
        siteNavigationSchema={viewOptions.siteNavigationSchema}
        pageHeader={viewOptions.pageHeader}
        // howToSchema={{
        //   show: !!contentObj?.howToSchema,
        //   data: contentObj?.howToSchema,
        // }}
        newsArticle={{
          show: !!contentObj?.newsArticle,
          data: contentObj?.newsArticle,
        }}
        
      >
        {contentObj ? (
          <div className={`${s.root} cmn_markdown_content`}>
            {contentObj?.hero || hero ? (
              <Hero
                content={contentObj?.hero || hero}
                widget={getWidget(contentObj?.productWidget1)}
              />
            ) : (
              <div className="cmn_hide" />
            )}

            {contentObj?.productWidget2 ? (
              <div className={`${s.fullwidth_container} cmn_container`}>
                <div className={s.widget_container}>
                  {getWidget(contentObj?.productWidget2)}
                </div>
              </div>
            ) : (
              <div className="cmn_hide" />
            )}

            {contentObj?.productWidget3 ? (
              <div className={`${s.fullwidth_container} cmn_container`}>
                <div className={s.widget_container}>
                  {getWidget(contentObj?.productWidget3)}
                </div>
              </div>
            ) : (
              <div className="cmn_hide" />
            )}

            {contentObj?.interlinking1 ? (
              <div className={`${s.fullwidth_container} cmn_container `}>
                <Interlinking
                  title={contentObj?.interlinking1?.data?.attributes?.title}
                  data={
                    contentObj?.interlinking1?.data?.attributes?.links?.Links ??
                    []
                  }
                  type={contentObj?.interlinking1?.data?.attributes?.type}
                />
              </div>
            ) : (
              <div className="cmn_hide" />
            )}

            <div
              className={`${s.fullwidth_container} ${s.jumbo_wrapper} cmn_container`}
            >
              <div className={s.center}>
                <Jumbo
                  //@ts-ignore
                  data={jumbo}
                />
              </div>
              <div className={s.right}>
                {contentObj?.interlinking2?.data && (
                  <div className={s.halfContainers}>
                    <Interlinking
                      title={
                        contentObj?.interlinking2?.data?.attributes?.title ||
                        contentObj?.interlinking2?.Title
                      }
                      data={
                        contentObj?.interlinking2?.data?.attributes?.links
                          ?.Links || contentObj?.interlinking2?.Links
                      }
                      type={
                        contentObj?.interlinking2?.data?.attributes?.type ||
                        contentObj?.interlinking2?.type
                      }
                    />
                  </div>
                )}

                {contentObj?.interlinking3?.data && (
                  <div className={s.halfContainers}>
                    <Interlinking
                      title={contentObj?.interlinking3?.data?.attributes?.title}
                      data={
                        contentObj?.interlinking3?.data?.attributes?.links
                          ?.Links ?? []
                      }
                      type={contentObj?.interlinking3?.data?.attributes?.type}
                    />
                  </div>
                )}
              </div>
            </div>

            {contentObj?.productWidget4 ? (
              <div className={`${s.fullwidth_container} cmn_container`}>
                <div className={s.widget_container}>
                  {getWidget(contentObj?.productWidget4)}
                </div>
              </div>
            ) : (
              <div className="cmn_hide" />
            )}

            {contentObj?.productWidget5 ? (
              <div className={`${s.fullwidth_container} cmn_container`}>
                <div className={s.widget_container}>
                  {getWidget(contentObj?.productWidget5)}
                </div>
              </div>
            ) : (
              <div className="cmn_hide" />
            )}

            <div className={s.footer}>
              {contentObj?.interlinking4 ? (
                <div className={cn(s.fullwidth_container, s.interlinking4)}>
                  <div className={cn('cmn_container', s.box)}>
                    <Interlinking
                      title={contentObj?.interlinking4?.data?.attributes?.title}
                      data={
                        contentObj?.interlinking4?.data?.attributes?.links
                          .Links ?? []
                      }
                      type={contentObj?.interlinking4?.data?.attributes?.type}
                    />
                  </div>
                </div>
              ) : (
                <div className="cmn_hide" />
              )}

              {contentObj?.faqContent ? (
                <div className={`${s.faq_div} markdown_content markdown_faq`}>
                  <Markdown className={s.faq_title}>
                    {contentObj?.faqTitle || ''}
                  </Markdown>
                  <Markdown className={s.faq_content}>
                    {contentObj.faqContent}
                  </Markdown>
                </div>
              ) : (
                <div className="cmn_hide" />
              )}
            </div>
          </div>
        ) : (
          <div className="cmn_error_div">Temporarily Unavailable</div>
        )}
        {showBannerModal && (
          <FuelBannerModal
            show={showBannerModal}
            onClose={() => setShowBannerModal(false)}
            pageUrl={contentObj?.seoCanonicalUrl}
          />
        )}
      </NavbarLayout>
    </>
  );
}
export default FlexibleArticleV2;
